import 'chartjs-plugin-datalabels'; // Plugin for adding number overlays on the bar/pie slices

import {
  FluidBarChartTranslationOptions,
  FluidChartInterface,
  FluidTheme,
  FluidThemeInterface,
  isNullOrUndefined,
} from '@lmig/fluid-core';
import { generateUniqueId } from '@lmig/fluid-core/lib/helpers/string.helpers';
import {
  Component,
  ComponentInterface,
  Element,
  h,
  Prop,
  State,
  Watch,
} from '@stencil/core';
import { Chart, ChartSize } from 'chart.js'; // Charting library, uses HTML canvas

import { ChartSubtitle, ChartTitle } from '../../common/fluid-chart.components';
import { useI18n } from '../../providers/fluid-i18n-provider/adapter/fluid-i18n.adapter';
import { FluidI18nStore } from '../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import {
  ChartUtils,
  FluidChartStyles,
} from '../../utils/charts/fluid-chart-utils';
import { Utils } from '../../utils/utils';

/**
 * @displayName Bar Chart
 *
 * @contextData FLUID Bar Chart is a data visualization component that displays data in a series of rectangular bars. It is typically used for comparing categorical or discrete data.
 */
@Component({
  tag: 'fluid-bar-chart',
  styleUrl: 'fluid-bar-chart.scss',
  shadow: true,
})
export class FluidBarChart
  implements FluidThemeInterface, FluidChartInterface, ComponentInterface
{
  // ================================================================ //
  // -- Own Properties (Private)

  /**
   * Intrinsic/extrinsic width of the chart.
   */
  private _elementWidth: number;

  /**
   * Reference to the <canvas> element that is used to render the chart.
   */
  private _chartRef: HTMLCanvasElement;

  /**
   * Reference to the Chart.js chart instance.
   */
  private _chart: Chart;

  // ================================================================ //
  // -- Host Element

  /**
   * Reference to the host element.
   */
  @Element() host: HTMLFluidBarChartElement;

  // ================================================================ //
  // -- Internationalisation

  /* @internal */
  @Prop({ mutable: true }) fluidTranslate: FluidBarChartTranslationOptions;
  private _translateId: string = generateUniqueId();
  @State() private _i18nStore: FluidI18nStore | undefined;

  // ================================================================ //
  // -- Props

  /**
   * What theme settings should the component use?
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  @Watch('theme')
  themeWatchHandler(newValue: string, oldValue: string) {
    // update colors only if colorOverride was not defined
    if (newValue !== oldValue && this._chart && !this.colorOverride) {
      const { colors } = FluidChartStyles[this.theme];

      this._chart.data.datasets[0].backgroundColor = colors;
      ChartUtils.updateChart(this._chart);
    }
  }

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) overrideGlobalTheme = false;

  /**
   * A unique identifier for your chart canvas element
   * Useful to specify for E2E, analytics, etc.
   * Defaults to a unique 36 char string
   */
  @Prop({ mutable: true }) chartId: string = generateUniqueId();

  /**
   * When provided, displays a heading title above the chart.
   */
  @Prop({ mutable: true }) chartTitle: string;

  /**
   * Supplementary subheading for the chart displayed below the "title".
   */
  @Prop({ mutable: true }) subTitle: string;

  /**
   * Takes an array of values for the bars in the chart
   */
  @Prop({ mutable: true }) chartData: number[];

  @Watch('chartData')
  chartDataWatchHandler(newValue: number[], oldValue: number[]) {
    if (newValue !== oldValue) {
      let { colors } = FluidChartStyles[this.theme];

      if (this.colorOverride) {
        colors = ChartUtils.createColorsSequence(
          this.colorOverride,
          this.chartData as []
        );
      }

      ChartUtils.dataHandler(this._chart, newValue, null, colors);
    }
  }

  /**
   * Takes an array of string labels for the bars in the chart
   */
  @Prop({ mutable: true }) chartLabels: string[];

  @Watch('chartLabels')
  chartLabelsWatchHandler(newValue: string[], oldValue: string[]) {
    if (newValue !== oldValue) ChartUtils.labelHandler(this._chart, newValue);
  }

  /**
   * Takes in chart hover data to be rendered as a tooltip
   */
  @Prop({ mutable: true }) chartHoverData: object[];

  @Watch('chartHoverData')
  chartHoverDataWatchHandler(newValue: object[], oldValue: object[]) {
    if (newValue !== oldValue)
      this._chart.options.tooltips.callbacks.afterBody = (tooltipItems) =>
        ChartUtils.tooltipAfterBody(tooltipItems, newValue);
    this.setChartLabels();
  }

  /**
   * Allows the user to pass in a unit of currency to prepend to to chart values
   * Defaults to an empty string if undefined, should you wish to not have currency in your units
   */
  @Prop({ mutable: true }) currencySymbol = '';

  /**
   * When provided, adds a white background with subtle drop shadow
   * to the chart's container.
   */
  @Prop({ mutable: true }) hasBackground = false;

  /**
   * A label to describe the data units in the chart (required).
   */
  @Prop({ mutable: true }) label = 'No label provided. What are your units?';

  /**
   * Allows the user to pass in a custom color scheme for the line graph.
   * If prop is not given the color scheme will default to styles in FluidChartStyles
   * When this is set the theme colors will be ignored
   */
  @Prop({ mutable: true }) colorOverride: string[];

  /**
   * Percent (0-1) of the available width each bar should be within the category width.
   * 1.0 will take the whole category width and put the bars right next to each other
   */
  @Prop({ mutable: true }) barPercentage = 0.9;

  /**
   * Percent (0-1) of the available width each category should be within the sample width
   */
  @Prop({ mutable: true }) categoryPercentage = 0.8;

  // ================================================================ //
  // -- Lifecycle Hooks

  /**
   * Called every time the component is connected to the DOM.
   * @action: setGlobalTheme - updates theme to match global theme & subscribes to changes
   */
  connectedCallback(): void {
    Utils.setGlobalTheme(this);
    useI18n.bind(this)({
      language: () => this.setChartLabels(),
    });
  }

  /**
   * Lifecycle Method to create the chart once the component loads
   *  data - takes in at least one dataset
   *  options - for styling and tweaking chart settings
   */
  componentDidLoad() {
    this._elementWidth = this.host.clientWidth;
    let { colors } = FluidChartStyles[this.theme];
    const { fonts } = FluidChartStyles[this.theme];

    // override colors and generate a colors sequence
    if (this.colorOverride) {
      colors = ChartUtils.createColorsSequence(
        this.colorOverride,
        this.chartData as []
      );
    }

    const myChartRef = this._chartRef.getContext('2d');
    this._chart = new Chart(myChartRef, {
      type: 'bar',
      data: {
        labels:
          this._i18nStore?.translate(this.fluidTranslate?.chartLabels) ||
          this.chartLabels,
        datasets: [
          {
            label:
              this._i18nStore?.translate(this.fluidTranslate?.label) ||
              this.label,
            data: this.chartData,
            backgroundColor: colors,
            barPercentage: this.barPercentage,
            categoryPercentage: this.categoryPercentage,
          },
        ],
      },
      options: {
        plugins: {
          // Controls the datalabels plugin
          datalabels: {
            display: true,
            font: (context) => ({
              size: ChartUtils.fontLabelScaler(context.chart.width),
              weight: 'bold',
              family: fonts,
            }),
            formatter: (value: number): string =>
              ChartUtils.toTruncated(value, 1, this.currencySymbol),
            anchor: 'end', // Attaches them to the top of the bar
            align: 'top', // Prevents them from sitting too low on the bar
          },
        },
        layout: {
          padding: {
            left: 0,
            right: 0,
            top: 30, // Needs to be a gap to display the datalabels on a bar that reaches the top of the chart
            bottom: 0,
          },
        },
        responsive: true,
        maintainAspectRatio: true,
        aspectRatio: 4 / 3,
        scales: {
          xAxes: [
            {
              gridLines: { display: false },
              ticks: {
                fontFamily: fonts,
                fontSize: ChartUtils.getChartBodyFontSize(this._elementWidth),
              },
            },
          ],
          yAxes: [
            {
              ticks: {
                beginAtZero: true,
                fontFamily: fonts,
                fontSize: ChartUtils.getChartBodyFontSize(this._elementWidth),
                callback: (value: number): string =>
                  ChartUtils.toTruncated(value, 0, this.currencySymbol), // axis truncated to whole number
              },
            },
          ],
        },
        title: {
          display: false, // We are using a custom element
        },
        legend: {
          display: false, // Deemed uncessesary
        },
        tooltips: {
          titleFontFamily: fonts,
          titleFontSize: ChartUtils.getChartBodyFontSize(this._elementWidth),
          bodyFontFamily: fonts,
          bodyFontSize: ChartUtils.getChartBodyFontSize(this._elementWidth),
          displayColors: false, // Deemed redundant and made the tooltip cramped
          callbacks: {
            // Modifies hover tooltip element with anything hoverData passes, designed to handle unlimited entries
            afterBody: (tooltipItems) =>
              ChartUtils.tooltipAfterBody(
                tooltipItems,
                this._i18nStore?.translate(
                  this.fluidTranslate?.chartHoverData
                ) || this.chartHoverData
              ),
            label: (tooltipItems, data) => {
              const value: any =
                data.datasets[tooltipItems.datasetIndex].data[
                  tooltipItems.index
                ];
              return `${
                this._i18nStore?.translate(this.fluidTranslate?.label) ||
                this.label
              }: ${this.currencySymbol}${value.toLocaleString()}`;
            },
          },
        },
        // @ts-ignore - This is a bug in the typings, the function signature is correct
        onResize: (chartInstance: Chart, size: ChartSize): void => {
          if (isNullOrUndefined(chartInstance)) return;
          this._elementWidth = size.width;
          ChartUtils.updateChart(chartInstance);
        },
      },
    });
    /**
     * Renders chart to scale font size based on the chart size
     * Re-renders chart with scaled font size for x and y axes
     */
    this._chart.render();
  }

  /**
   * Called every time the component is disconnected from the DOM.
   */
  disconnectedCallback() {
    this._i18nStore?.deregisterElement(this._translateId);
  }

  // ================================================================ //
  // -- Local Methods

  /**
   * Updates the chart labels whenever the language changes in the i18n provider.
   */
  setChartLabels() {
    this._chart.data.labels =
      this._i18nStore?.translate(this.fluidTranslate?.chartLabels) ||
      this.chartLabels;
    this._chart.update();
  }

  /**
   * Used for calculating relative sizes
   * @return the chart element
   */
  getChart() {
    return this._chart;
  }

  /**
   * Used for calculating relative sizes
   * @return The width of the containing element
   */
  getElementWidth(): number {
    return this._chart?.width > 0 ? this._chart.width : undefined;
  }

  // ================================================================ //
  // -- Render (Main)

  render() {
    return (
      <div
        class={{
          [`fluid-bar-chart theme-${this.theme}`]: true,
          'has-background': this.hasBackground,
        }}>
        <ChartTitle
          title={this.chartTitle}
          fluidTranslate={this.fluidTranslate}
          i18nStore={this._i18nStore}
          theme={this.theme}
        />
        <ChartSubtitle
          subTitle={this.subTitle}
          fluidTranslate={this.fluidTranslate}
          i18nStore={this._i18nStore}
          theme={this.theme}
        />
        <canvas
          id={this.chartId}
          ref={(el: HTMLCanvasElement) => (this._chartRef = el)}
        />
      </div>
    );
  }
}
